import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { googleLogout } from '@react-oauth/google'
import { MdPerson, MdLogout, MdCardMembership } from 'react-icons/md'
import { FiLogOut } from 'react-icons/fi'
import { GiHamburgerMenu } from 'react-icons/gi'
import { FaCoins } from 'react-icons/fa'
import { TbPointFilled } from 'react-icons/tb'
import DunoLogo from '../../assests/duno_main_logo.png'
import Profile from '../../assests/Profile-PNG-File.png'
import { baseurl } from '../Url'
import { fetchUserDetails } from '../../Slices/UserSlice'
import { logActivity } from '../../services/activityService'
import style from './SidebarHeader.module.css'

const SidebarHeader = ({
  onMenuToggle,
  profileSlot,
  desktopProfileSlot,
  showMenuIcon,
  showInlineProfileIcon = false,
  usePortalsForProfileIcon = true,
  showSidebarAccountMenu = false,
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const popupRef = useRef(null)
  const { sessionExpired, userDetails } = useSelector((state) => state.user)

  const UserID = localStorage.getItem('USERID')
  const OrgID = localStorage.getItem('ORGID')
  const key = (() => {
    try {
      const v = localStorage.getItem('access-token')
      return v && v !== 'undefined' ? JSON.parse(v) : null
    } catch {
      return null
    }
  })()

  const [profilePopup, setProfilePopup] = useState(false)
  const [logout, setLogout] = useState(false)
  const [profileImageUrl, setProfileImageUrl] = useState('')
  const [imageError, setImageError] = useState(false)
  const [profileCompleted, setProfileCompleted] = useState(null)
  const [goalCompleted, setGoalCompleted] = useState(null)
  const [rewardPoints, setRewardPoints] = useState(0)
  const [rewardMoney, setRewardMoney] = useState(0)

  const withImageBust = (url, ts = Date.now()) => {
    if (!url) return ''
    const normalizedUrl = String(url).trim()
    if (normalizedUrl.startsWith('data:')) return normalizedUrl
    return `${normalizedUrl}${normalizedUrl.includes('?') ? '&' : '?'}t=${ts}`
  }
  const resolveProfileImageSrc = () => {
    if (!profileImageUrl || imageError) return Profile
    const normalizedUrl = String(profileImageUrl).trim()
    if (normalizedUrl.startsWith('data:')) return normalizedUrl
    return `${normalizedUrl}${normalizedUrl.includes('?') ? '&' : '?'}uid=${UserID || ''}`
  }

  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('completionSyncData') || '{}')
      if (cached.profile_completion !== undefined) setProfileCompleted(cached.profile_completion)
      if (cached.goals_completion !== undefined) setGoalCompleted(cached.goals_completion)
    } catch {}

    const handleCompletionUpdate = (event) => {
      const detail = event?.detail || {}
      if (detail.profile_completion !== undefined) setProfileCompleted(detail.profile_completion)
      if (detail.goals_completion !== undefined) setGoalCompleted(detail.goals_completion)
    }

    window.addEventListener('duno-completion-updated', handleCompletionUpdate)
    return () => window.removeEventListener('duno-completion-updated', handleCompletionUpdate)
  }, [])

  const handleProfileNavigation = (e) => {
    e.stopPropagation()
    setProfilePopup(!profilePopup)
    window.dispatchEvent(new CustomEvent('duno-dashboard-setup-open'))
  }

  const handleMyAccountClick = () => {
    logActivity({ module: 'PROFILE', action: 'MY_ACCOUNT_CLICK', remark: 'My Account from profile popup' })
    const completion = Number(profileCompleted ?? 0)
    const isNewUser = Number.isFinite(completion) && completion <= 0
    if (isNewUser) {
      navigate('/profile')
    } else {
      navigate('/profile/profile-details', { state: { userID: UserID } })
    }
    setProfilePopup(false)
  }

  const handleLogout = (e) => {
    e.preventDefault()
    const isMentor = localStorage.getItem('mentor') === 'true'
    logActivity({ module: 'AUTH', action: 'LOGOUT', remark: 'User logged out' })
    localStorage.removeItem('access-token')
    localStorage.removeItem('ClubID')
    localStorage.removeItem('ORGID')
    localStorage.removeItem('USERID')
    localStorage.removeItem('mentor')
    localStorage.removeItem('roleId')
    localStorage.removeItem('learnertoken')
    localStorage.removeItem('learneruserid')
    localStorage.removeItem('username')
    localStorage.removeItem('newLearningId')
    localStorage.removeItem('MainLevelId')
    localStorage.removeItem('SubLevelId')
    localStorage.removeItem('clickedItem')
    localStorage.removeItem('clickeditemDes')
    localStorage.removeItem('functionCalled')
    localStorage.removeItem('rzp_checkout_anon_id')
    localStorage.removeItem('rzp_device_id')
    localStorage.removeItem('countrydetails')
    localStorage.removeItem('timezone')
    localStorage.removeItem('currency')
    localStorage.removeItem('adminmapped')
    localStorage.removeItem('dailySnoozeTriggered')
    localStorage.removeItem('weeklySnoozeTriggered')
    localStorage.removeItem('hasShownDailyDrill')
    localStorage.removeItem('trialUser')
    localStorage.removeItem('categoryID')
    googleLogout()
    navigate(isMentor ? '/login/mentor' : '/login')
    window.location.reload()
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setProfilePopup(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('profileImageSyncData') || '{}')
      if (cached.user_id && String(cached.user_id) !== String(UserID)) {
        localStorage.removeItem('profileImageSyncData')
      } else if (cached.profile_image) {
        setProfileImageUrl(withImageBust(cached.profile_image, cached.ts))
        setImageError(false)
      }
    } catch {}

    const handleProfileImageUpdate = (event) => {
      const detail = event?.detail || {}
      if (detail.user_id && String(detail.user_id) !== String(UserID)) return
      if (detail.profile_image) {
        setProfileImageUrl(withImageBust(detail.profile_image, detail.ts))
        setImageError(false)
      }
    }

    window.addEventListener('duno-profile-image-updated', handleProfileImageUpdate)
    return () => window.removeEventListener('duno-profile-image-updated', handleProfileImageUpdate)
  }, [])

  useEffect(() => {
    const fetchProfileImage = async () => {
      if (UserID) {
        try {
          const response = await fetch(`${baseurl}/user/profile`, {
            method: 'POST',
            body: JSON.stringify({ user_id: UserID, org_id: OrgID }),
            headers: { 'Content-Type': 'application/json' },
          })
          const data = await response.json()
          if (data.status === true && data.data?.profile_image?.trim()) {
            const imagePayload = { profile_image: data.data.profile_image, ts: Date.now(), user_id: String(UserID) }
            setProfileImageUrl(withImageBust(imagePayload.profile_image, imagePayload.ts))
            localStorage.setItem('profileImageSyncData', JSON.stringify(imagePayload))
            setImageError(false)
          } else {
            setProfileImageUrl('')
            setImageError(false)
          }
        } catch {
          setProfileImageUrl('')
          setImageError(false)
        }
      } else {
        setProfileImageUrl('')
        setImageError(false)
      }
    }
    fetchProfileImage()
  }, [UserID, OrgID])

  useEffect(() => {
    const fetchCompletion = async () => {
      if (!UserID || !OrgID || !key) return
      try {
        const response = await fetch(`${baseurl}/banner/data/retrive`, {
          method: 'POST',
          body: JSON.stringify({ user_id: UserID, org_id: OrgID }),
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${key}` },
        })
        const data = await response.json()
        if (data.status === true && data.data) {
          const completionPayload = {}
          if (data.data.profile_completion !== undefined) {
            setProfileCompleted(data.data.profile_completion)
            completionPayload.profile_completion = data.data.profile_completion
          }
          if (data.data.goals_completion !== undefined) {
            setGoalCompleted(data.data.goals_completion)
            completionPayload.goals_completion = data.data.goals_completion
          }
          if (Object.keys(completionPayload).length > 0) {
            localStorage.setItem('completionSyncData', JSON.stringify(completionPayload))
            window.dispatchEvent(new CustomEvent('duno-completion-updated', { detail: completionPayload }))
          }
        }
      } catch (err) {
        console.error('Error fetching completion:', err)
      }
    }
    fetchCompletion()
  }, [UserID, OrgID, key])

  useEffect(() => {
    if (UserID) {
      dispatch(fetchUserDetails({ userid: UserID, token: key }))
    } else {
      localStorage.removeItem('access-token')
      localStorage.removeItem('ClubID')
      localStorage.removeItem('ORGID')
      localStorage.removeItem('USERID')
      navigate('/login')
    }
  }, [UserID, dispatch, navigate, key])

  const ProfileIcon = ({ gradientId = 'default' }) => (
    <div className={style.userIconWrap} onClick={handleProfileNavigation}>
      <div className={style.userIcon}>
        <img
          src={resolveProfileImageSrc()}
          alt="user"
          key={`header-profile-img-${UserID || 'none'}`}
          onError={() => setImageError(true)}
          onLoad={() => setImageError(false)}
        />
      </div>
      <svg className={style.progressRings} viewBox="0 0 52 52" aria-hidden="true">
        <defs>
          <linearGradient id={`${gradientId}-complete`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0151CD" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
          <linearGradient id={`${gradientId}-zero`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1e3a8a" />
            <stop offset="100%" stopColor="#93c5fd" />
          </linearGradient>
        </defs>
        <circle className={`${style.ringTrack} ${style.ringTrackOuter}`} cx="26" cy="26" r="24" />
        <circle
          className={style.ringGoals}
          cx="26" cy="26" r="24"
          style={{
            strokeDashoffset: (goalCompleted ?? 0) === 0 ? 0 : 151 * (1 - (goalCompleted ?? 0) / 100),
            stroke: (goalCompleted ?? 0) >= 100 ? `url(#${gradientId}-complete)` : `url(#${gradientId}-zero)`,
          }}
        />
        <circle className={`${style.ringTrack} ${style.ringTrackInner}`} cx="26" cy="26" r="21.5" />
        <circle
          className={style.ringProfile}
          cx="26" cy="26" r="21.5"
          style={{
            strokeDashoffset: (profileCompleted ?? 0) === 0 ? 0 : 135 * (1 - (profileCompleted ?? 0) / 100),
            stroke: (profileCompleted ?? 0) >= 100 ? `url(#${gradientId}-complete)` : `url(#${gradientId}-zero)`,
          }}
        />
      </svg>
    </div>
  )

  const displayName =
    userDetails?.name ||
    userDetails?.full_name ||
    userDetails?.username ||
    userDetails?.user_name ||
    userDetails?.userName ||
    localStorage.getItem('username') ||
    'My Account'

  const displayEmail =
    userDetails?.email ||
    userDetails?.user_email ||
    userDetails?.mail ||
    ''

  const profilePopupContent = (
    <div
      ref={popupRef}
      className={`${style.profilePopup} ${!usePortalsForProfileIcon ? style.profilePopupInline : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={style.profilePopupHeader}>
        <div className={style.profilePopupAvatar}>
          <img
            src={resolveProfileImageSrc()}
            alt="user"
            onError={() => setImageError(true)}
            onLoad={() => setImageError(false)}
          />
        </div>
        <div className={style.profilePopupHeaderText}>
          <p className={style.profilePopupName}>{displayName}</p>
          {displayEmail ? <span className={style.profilePopupEmail}>{displayEmail}</span> : null}
        </div>
      </div>
      <div
        className={`${style.profilePopupSection} ${style.profilePopupSectionClickable}`}
        onClick={() => {
          logActivity({ module: 'PROFILE', action: 'PROFILE_COMPLETION_CLICK', remark: 'Profile Edit from profile popup' })
          navigate('/profile')
          setProfilePopup(false)
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            navigate('/profile')
            setProfilePopup(false)
          }
        }}
      >
        <h4 className={style.profilePopupSectionTitle}>Profile Completion</h4>
        <div className={style.profilePopupProgressRow}>
          <span className={style.profilePopupLabel}>Profile</span>
          <span className={style.profilePopupPercent}>{profileCompleted ?? 0}%</span>
        </div>
        <div className={style.profilePopupBar}>
          <div
            className={style.profilePopupBarFill}
            style={{
              width: `${profileCompleted ?? 0}%`,
              background: (profileCompleted ?? 0) >= 100 ? 'linear-gradient(90deg, #0151CD, #7C3AED)' : 'linear-gradient(90deg, #1e3a8a, #93c5fd)',
            }}
          />
        </div>
        <span
          className={style.profilePopupStatus}
          style={{
            color: (profileCompleted ?? 0) >= 100 ? '#7C3AED' : '#1e3a8a',
            background:
              (profileCompleted ?? 0) >= 100
                ? 'linear-gradient(135deg, rgba(1, 81, 205, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)'
                : 'linear-gradient(135deg, rgba(30, 58, 138, 0.2) 0%, rgba(147, 197, 253, 0.3) 100%)',
          }}
        >
          {(profileCompleted ?? 0) >= 100 ? 'Complete' : (profileCompleted ?? 0) === 0 ? 'Not started' : `${profileCompleted ?? 0}% Completed`}
        </span>
        <button
          className={style.profilePopupUpdateBtn}
          onClick={(e) => {
            e.stopPropagation()
            logActivity({ module: 'PROFILE', action: 'PROFILE_COMPLETION_CLICK', remark: 'Update profile from popup button' })
            navigate('/profile')
            setProfilePopup(false)
          }}
          type="button"
        >
          Update Profile
        </button>
      </div>
      <div
        className={`${style.profilePopupSection} ${style.profilePopupSectionClickable}`}
        onClick={() => {
          logActivity({ module: 'PROFILE', action: 'GOALS_PROGRESS_CLICK', remark: 'Goal Setting from profile popup' })
          navigate('/profile', { state: { openGoalsPopup: true } })
          setProfilePopup(false)
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            navigate('/profile', { state: { openGoalsPopup: true } })
            setProfilePopup(false)
          }
        }}
      >
        <h4 className={style.profilePopupSectionTitle}>Goals Progress</h4>
        <div className={style.profilePopupProgressRow}>
          <span className={style.profilePopupLabel}>Goals</span>
          <span className={style.profilePopupPercent}>{goalCompleted ?? 0}%</span>
        </div>
        <div className={style.profilePopupBar}>
          <div
            className={style.profilePopupBarFill}
            style={{
              width: `${goalCompleted ?? 0}%`,
              background: (goalCompleted ?? 0) >= 100 ? 'linear-gradient(90deg, #0151CD, #7C3AED)' : 'linear-gradient(90deg, #1e3a8a, #93c5fd)',
            }}
          />
        </div>
        <span
          className={style.profilePopupStatus}
          style={{
            color: (goalCompleted ?? 0) >= 100 ? '#7C3AED' : '#1e3a8a',
            background:
              (goalCompleted ?? 0) >= 100
                ? 'linear-gradient(135deg, rgba(1, 81, 205, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)'
                : 'linear-gradient(135deg, rgba(30, 58, 138, 0.2) 0%, rgba(147, 197, 253, 0.3) 100%)',
          }}
        >
          {(goalCompleted ?? 0) >= 100 ? 'Complete' : (goalCompleted ?? 0) === 0 ? 'Not started' : `${goalCompleted ?? 0}% Completed`}
        </span>
        <button
          className={style.profilePopupUpdateBtn}
          onClick={(e) => {
            e.stopPropagation()
            logActivity({ module: 'PROFILE', action: 'GOALS_PROGRESS_CLICK', remark: 'Update goals from popup button' })
            navigate('/profile', { state: { openGoalsPopup: true } })
            setProfilePopup(false)
          }}
          type="button"
        >
          Update Career Goals
        </button>
      </div>
      <div className={style.profilePopupDivider} />
      <div className={style.profilePopupItem} onClick={handleMyAccountClick}>
        <div className={style.profilePopupIcon}>
          <MdPerson size={20} />
        </div>
        <p>My Account</p>
      </div>
      <div
        className={style.profilePopupItem}
        onClick={() => {
          logActivity({ module: 'PROFILE', action: 'MY_SUBSCRIPTION_CLICK', remark: 'My Subscription from profile popup' })
          navigate('/subscribe/details')
          setProfilePopup(false)
        }}
      >
        <div className={style.profilePopupIcon}>
          <MdCardMembership size={20} />
        </div>
        <p>My Subscription</p>
      </div>
      <div
        className={style.profilePopupItem}
        onClick={() => {
          logActivity({ module: 'PROFILE', action: 'LOGOUT_CLICK', remark: 'Logout initiated from profile popup' })
          setLogout(true)
          setProfilePopup(false)
        }}
      >
        <div className={style.profilePopupIcon}>
          <MdLogout size={20} />
        </div>
        <p>Logout</p>
      </div>
    </div>
  )

  const sidebarAccountMenuContent = (
    <div className={style.sidebarAccountMenu}>
      <div className={style.sidebarAccountTop}>
        <div className={style.profilePopupAvatar}>
          <img
            src={resolveProfileImageSrc()}
            alt="user"
            onError={() => setImageError(true)}
            onLoad={() => setImageError(false)}
          />
        </div>
        <div className={style.profilePopupHeaderText}>
          <p className={style.profilePopupName}>{displayName}</p>
          {displayEmail ? <span className={style.profilePopupEmail}>{displayEmail}</span> : null}
        </div>
      </div>

      <div
        className={`${style.profilePopupSection} ${style.profilePopupSectionClickable} ${style.sidebarAccountSection}`}
        onClick={() => {
          logActivity({ module: 'PROFILE', action: 'PROFILE_COMPLETION_CLICK', remark: 'Profile Edit from sidebar account menu' })
          navigate('/profile')
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') navigate('/profile')
        }}
      >
        <h4 className={style.profilePopupSectionTitle}>Profile Completion</h4>
        <div className={style.profilePopupProgressRow}>
          <span className={style.profilePopupLabel}>Profile</span>
          <span className={style.profilePopupPercent}>{profileCompleted ?? 0}%</span>
        </div>
        <div className={style.profilePopupBar}>
          <div
            className={style.profilePopupBarFill}
            style={{
              width: `${profileCompleted ?? 0}%`,
              background: (profileCompleted ?? 0) >= 100 ? 'linear-gradient(90deg, #0151CD, #7C3AED)' : 'linear-gradient(90deg, #1e3a8a, #93c5fd)',
            }}
          />
        </div>
      </div>

      <div
        className={`${style.profilePopupSection} ${style.profilePopupSectionClickable} ${style.sidebarAccountSection}`}
        onClick={() => {
          logActivity({ module: 'PROFILE', action: 'GOALS_PROGRESS_CLICK', remark: 'Goals Progress from sidebar account menu' })
          navigate('/profile', { state: { openGoalsPopup: true } })
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') navigate('/profile', { state: { openGoalsPopup: true } })
        }}
      >
        <h4 className={style.profilePopupSectionTitle}>Goals Progress</h4>
        <div className={style.profilePopupProgressRow}>
          <span className={style.profilePopupLabel}>Goals</span>
          <span className={style.profilePopupPercent}>{goalCompleted ?? 0}%</span>
        </div>
        <div className={style.profilePopupBar}>
          <div
            className={style.profilePopupBarFill}
            style={{
              width: `${goalCompleted ?? 0}%`,
              background: (goalCompleted ?? 0) >= 100 ? 'linear-gradient(90deg, #0151CD, #7C3AED)' : 'linear-gradient(90deg, #1e3a8a, #93c5fd)',
            }}
          />
        </div>
      </div>

      <div className={style.profilePopupDivider} />

      <div className={`${style.profilePopupItem} ${style.sidebarAccountItem}`} onClick={handleMyAccountClick}>
        <div className={style.profilePopupIcon}>
          <MdPerson size={20} />
        </div>
        <p>My Account</p>
      </div>
      <div
        className={`${style.profilePopupItem} ${style.sidebarAccountItem}`}
        onClick={() => {
          logActivity({ module: 'PROFILE', action: 'MY_SUBSCRIPTION_CLICK', remark: 'My Subscription from sidebar account menu' })
          navigate('/subscribe/details')
        }}
      >
        <div className={style.profilePopupIcon}>
          <MdCardMembership size={20} />
        </div>
        <p>My Subscription</p>
      </div>
      <div
        className={`${style.profilePopupItem} ${style.sidebarAccountItem}`}
        onClick={() => {
          logActivity({ module: 'PROFILE', action: 'LOGOUT_CLICK', remark: 'Logout initiated from sidebar account menu' })
          setLogout(true)
        }}
      >
        <div className={style.profilePopupIcon}>
          <MdLogout size={20} />
        </div>
        <p>Logout</p>
      </div>
    </div>
  )

  return (
    <>
      <div className={style.sidebarHeader}>
        <div className={style.logoRow}>
          {showMenuIcon && (
            <button type="button" className={style.menuIcon} onClick={onMenuToggle} aria-label="Open menu">
              <GiHamburgerMenu size={22} />
            </button>
          )}
          <div className={style.logoWrap} onClick={() => navigate('/')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate('/')}>
            <img src={DunoLogo} alt="Duno Club" />
          </div>
        </div>
        {showInlineProfileIcon && (
          <div className={style.inlineProfileIcon}>
            <ProfileIcon gradientId="sidebarInlineProfileGrad" />
          </div>
        )}
      </div>
      {showSidebarAccountMenu ? sidebarAccountMenuContent : null}
      {usePortalsForProfileIcon &&
        desktopProfileSlot &&
        createPortal(
          <div className={style.desktopProfileSlot}>
            <ProfileIcon gradientId="desktopProfileGrad" />
          </div>,
          desktopProfileSlot
        )}
      {usePortalsForProfileIcon &&
        profileSlot &&
        createPortal(
          <div className={style.mobileProfileSlot}>
            <ProfileIcon gradientId="mobileProfileGrad" />
          </div>,
          profileSlot
        )}
      {usePortalsForProfileIcon &&
        profilePopup &&
        createPortal(profilePopupContent, document.body)}
      {(logout || sessionExpired) &&
        createPortal(
          <>
            <div className={style.popupOverlay} onClick={() => !sessionExpired && setLogout(false)} />
            <div className={style.Popup}>
              <div className={style.popupIconArea}>
                <FiLogOut />
              </div>
              <div className={style.popupText}>
                <p>{sessionExpired ? 'Session Expired' : 'Log out of Duno?'}</p>
                <span>
                  {sessionExpired ? 'Your session has expired. Please log in again to continue.' : "You'll need to sign in again to access your account."}
                </span>
              </div>
              <div className={style.PopupButton}>
                {sessionExpired ? (
                  <button className={style.noButton} onClick={(e) => handleLogout(e)}>
                    Login Again
                  </button>
                ) : (
                  <>
                    <button className={style.noButton} onClick={() => setLogout(false)}>
                      Stay
                    </button>
                    <button className={style.yesButton} onClick={(e) => handleLogout(e)}>
                      Log out
                    </button>
                  </>
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  )
}

export default SidebarHeader
